import { formatDate } from '@/lib/format';
import { leadStatus, orderStatus } from '@/theme/status';
import type { Channel, Direccion, EstadoLead, OrigenEstadoLead } from '@/types/domain';
import type {
  CambioLead,
  Kanban,
  KanbanColumna,
  LeadAnalisisDetalle,
  LeadCard,
  LeadDetalle,
  LeadHistorialEntry,
  LeadMensajeReciente,
  LeadMotivo,
  LeadOrdenResumen,
  LeadSignal,
  ListaLeads,
  PurgaEjemplo,
  PurgaLeadsResultado,
  RecalculoLeadResultado,
  RecalculoMasivoResultado,
} from '@/types/crm';

/**
 * Todo el formateo del CRM, sin React ni red (así el smoke lo puede probar a mano).
 *
 * Es el mismo criterio que `orders-format.ts`: lo que la pantalla DICE sale de acá y los
 * componentes solo pintan. Cada feature trae sus helpers en vez de importarlos de otra pantalla
 * (las únicas importaciones cruzadas del repo son las de `@/features/auth`), así que
 * `formatNumber`, `formatRelativeTime` o `collapseText` viven también en Órdenes/Bandeja con el
 * mismo criterio: duplicar tres líneas es más barato que acoplar dos pantallas.
 */

/** Cuántas tarjetas se piden por columna (el backend acepta 1-200 y su default es 30). */
export const KANBAN_LIMITS = [10, 30, 50, 100] as const;
export const KANBAN_LIMIT_DEFAULT = 30;

/** Cuántos leads se piden en la vista de tabla (1-200, default 50). */
export const LEADS_LIMITS = [25, 50, 100, 200] as const;
export const LEADS_LIMIT_DEFAULT = 50;

/** Tope del recálculo masivo (1-2000, default 500). */
export const RECALCULO_LIMIT_DEFAULT = 500;
export const RECALCULO_LIMIT_MAX = 2000;

/** Purga de conversaciones (`POST .../crm/purgar`, solo ADMIN): espejo de `crm.service.ts`. */
export const PURGA_DIAS_DEFAULT = 90;
export const PURGA_DIAS_MAX = 3650;
export const PURGA_LIMITE_DEFAULT = 500;
export const PURGA_LIMITE_MAX = 5000;

/** Topes de la vista previa y del motivo, iguales a los del backend. */
export const PREVIEW_MAX_CHARS = 90;
export const MOTIVO_MAX_CHARS = 300;

/** Lo que el backend guarda cuando la reclasificación manual no lleva motivo. */
export const MOTIVO_MANUAL_POR_DEFECTO = 'Reclasificado a mano por el equipo';

/** Nombre visible de cada canal (el enum del backend es `Channel`). */
export const channelLabel: Record<Channel, string> = {
  WHATSAPP: 'WhatsApp',
  INSTAGRAM: 'Instagram',
  MESSENGER: 'Messenger',
  TIKTOK: 'TikTok',
};

/** Opciones del filtro de canal (`''` = todos). TikTok queda fuera: no se ofrece en el panel. */
export const CHANNEL_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Todos los canales' },
  { value: 'WHATSAPP', label: channelLabel.WHATSAPP },
  { value: 'INSTAGRAM', label: channelLabel.INSTAGRAM },
  { value: 'MESSENGER', label: channelLabel.MESSENGER },
];

/** Quién puso el estado: el clasificador (`AUTO`) o el equipo (`MANUAL`). */
export const originLabel: Record<OrigenEstadoLead, string> = {
  AUTO: 'Automático',
  MANUAL: 'Manual',
};

/** Opciones del filtro de origen (`''` = todos). */
export const ORIGIN_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Automático y manual' },
  { value: 'AUTO', label: 'Solo automáticos' },
  { value: 'MANUAL', label: 'Solo reclasificados a mano' },
];

/** Filtro del bot: `''` = todos, `'atencion'` = bot pausado (requiere atención), `'bot'` = activo. */
export type AttentionFilter = '' | 'atencion' | 'bot';

export const ATTENTION_FILTERS: { value: AttentionFilter; label: string }[] = [
  { value: '', label: 'Bot activo o pausado' },
  { value: 'atencion', label: 'Requiere atención humana' },
  { value: 'bot', label: 'Atendido por el bot' },
];

/** El filtro de la barra → el `botActivo` del contrato. */
export function attentionToBotActivo(value: AttentionFilter): boolean | undefined {
  if (value === 'atencion') return false;
  if (value === 'bot') return true;
  return undefined;
}

/** Qué señal se encontró en los mensajes del cliente (se pinta en el detalle). */
export const LEAD_SIGNAL_LABEL: Record<LeadSignal, string> = {
  cantidad: 'Dio una cantidad',
  variante: 'Eligió una variante',
  datos_de_envio: 'Mandó los datos de envío',
  confirmacion: 'Confirmó el resumen',
  producto_mencionado: 'Mencionó un producto',
  interes: 'Preguntó por el producto',
  foto: 'Mandó una foto',
  saludo: 'Solo saludó',
};

/** Por qué el clasificador dejó el lead en ese estado. */
export const LEAD_MOTIVO_LABEL: Record<LeadMotivo, string> = {
  orden_creada: 'Tiene una orden creada',
  datos_de_envio: 'Dio los datos de envío',
  cantidad_o_variante: 'Dio cantidad o eligió una variante',
  interes_sin_cantidad: 'Preguntó sin dar cantidad',
  sin_senales_de_compra: 'Escribió sin señales de compra',
  sin_mensajes_entrantes: 'Sin mensajes entrantes de texto',
  manual: 'Reclasificado a mano por el equipo',
};

/** Todo el texto en una línea (el kanban no puede crecer de alto por un mensaje largo). */
export function collapseText(text: string, max = PREVIEW_MAX_CHARS): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;

  return `${flat.slice(0, max - 1).trimEnd()}…`;
}

/** Separador de miles del panel (`es-CO`). */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('es-CO').format(value);
}

/** "1 lead" / "3 leads": una cantidad sin sustantivo no se entiende. */
export function countLabel(count: number, singular: string, plural: string): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}

/**
 * Cómo se llama el contacto.
 *
 * El contrato del CRM NO trae nombre (la conversación solo tiene `externalContactId`), así que se
 * muestra ese identificador: es el teléfono en WhatsApp y el PSID en el resto de canales.
 */
export function contactLabel(lead: Pick<LeadCard, 'externalContactId'>): string {
  return lead.externalContactId;
}

/**
 * "Hace 5 min", "Hace 3 h", "Ayer", "16 sept 2026".
 *
 * El CRM ordena por actividad reciente, así que lo que dice si un lead está vivo es cuánto hace de
 * su última actividad; pasada la semana se cae a la fecha.
 */
export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return 'Sin actividad';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diff = Date.now() - date.getTime();
  // Reloj adelantado (o fecha futura): se pinta la fecha en vez de un "en 3 min" que no existe.
  if (diff < 0) return formatDate(value);

  const minutos = Math.floor(diff / 60_000);
  if (minutos < 1) return 'Ahora';
  if (minutos < 60) return `Hace ${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `Hace ${horas} h`;

  const dias = Math.floor(horas / 24);
  if (dias === 1) return 'Ayer';
  if (dias < 7) return `Hace ${dias} días`;

  return formatDate(value);
}

/** De quién es el último mensaje ("Cliente"/"Negocio"), para la vista previa de la tarjeta. */
export const messageAuthorLabel: Record<Direccion, string> = {
  IN: 'Cliente',
  OUT: 'Negocio',
};

/** "Cliente: quiero 2 blusas" — el último mensaje, en una línea; `null` si no hay mensajes. */
export function describeLeadPreview(lead: Pick<LeadCard, 'ultimoMensaje'>): string | null {
  const mensaje = lead.ultimoMensaje;
  if (!mensaje) return null;

  const texto = collapseText(mensaje.contenido);
  if (texto.length === 0) return `${messageAuthorLabel[mensaje.direccion]}: (sin texto)`;

  return `${messageAuthorLabel[mensaje.direccion]}: ${texto}`;
}

/** "3 mensajes · 1 orden" — el tamaño de la conversación, como se lee en la tarjeta. */
export function describeLeadStats(lead: Pick<LeadCard, 'mensajesTotal' | 'ordenesTotal'>): string {
  return `${countLabel(lead.mensajesTotal, 'mensaje', 'mensajes')} · ${countLabel(
    lead.ordenesTotal,
    'orden',
    'órdenes',
  )}`;
}

/** Todo el texto por el que se puede encontrar un lead en el buscador de la pantalla. */
export function leadSearchText(lead: LeadCard): string {
  return [
    lead.externalContactId,
    lead.estadoLeadMotivo ?? '',
    lead.channel,
    channelLabel[lead.channel],
    lead.adId ?? '',
    lead.campaignId ?? '',
    lead.ultimaOrden?.clienteNombre ?? '',
    lead.ultimoMensaje?.contenido ?? '',
    leadStatus[lead.estadoLead].label,
    originLabel[lead.estadoLeadOrigen],
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Buscador del cliente sobre lo que ya está cargado.
 *
 * El endpoint tiene `buscar`, pero solo compara contra el identificador de contacto; la barra de la
 * pantalla busca en todo lo visible (mensaje, motivo, campaña, cliente de la orden), así que el
 * filtro se hace acá — igual que en Órdenes.
 */
export function filterLeads(leads: LeadCard[], search: string): LeadCard[] {
  const needle = search.trim().toLowerCase();
  if (needle.length === 0) return leads;

  return leads.filter((lead) => leadSearchText(lead).includes(needle));
}

/** Las señales del detalle, en texto: "Dio una cantidad, Eligió una variante". */
export function describeLeadSignals(senales: LeadSignal[]): string {
  if (senales.length === 0) return 'El clasificador no encontró señales en el chat.';

  return senales.map((senal) => LEAD_SIGNAL_LABEL[senal]).join(', ');
}

/**
 * El motivo con el que quedó el lead, ya legible.
 *
 * `estadoLeadMotivo` es texto libre: si lo dejó el clasificador coincide con un `LeadMotivo`
 * conocido y se traduce; si lo escribió el equipo, se muestra tal cual.
 */
export function describeLeadMotivo(motivo: string | null): string {
  if (!motivo) return 'Sin motivo registrado';

  return LEAD_MOTIVO_LABEL[motivo as LeadMotivo] ?? motivo;
}

/** Los datos concretos del análisis, solo los que existen (para pintarlos como lista). */
export function describeLeadAnalysis(detalle: LeadAnalisisDetalle): string[] {
  const lineas: string[] = [];

  if (detalle.cantidad !== null) {
    lineas.push(`Cantidad detectada: ${formatNumber(detalle.cantidad)}`);
  }
  if (detalle.variantes.length > 0) lineas.push(`Variantes: ${detalle.variantes.join(', ')}`);
  if (detalle.productos.length > 0) lineas.push(`Productos: ${detalle.productos.join(', ')}`);
  lineas.push(
    `Mensajes entrantes analizados: ${formatNumber(detalle.mensajesAnalizados)} (tope 50)`,
  );

  return lineas;
}

/**
 * "Hoy el clasificador diría «Tibio» (Preguntó sin dar cantidad)."
 *
 * Es la comparación que explica si un lead está reclasificado a mano: si el estado real no coincide
 * con el automático, alguien lo movió y el botón de recálculo tiene algo que hacer.
 */
export function describeAutomaticClassification(detalle: LeadDetalle): string {
  const automatico = leadStatus[detalle.clasificacionAutomatica.estadoLead].label;
  const motivo = LEAD_MOTIVO_LABEL[detalle.clasificacionAutomatica.motivo];

  return `Hoy el clasificador diría «${automatico}» (${motivo}).`;
}

/** Fila "Estado" del detalle: quién puso el estado y cuándo. */
export function describeLeadOrigin(
  lead: Pick<LeadCard, 'estadoLeadOrigen' | 'estadoLeadUpdatedAt'>,
): string {
  return `${originLabel[lead.estadoLeadOrigen]} · ${formatRelativeTime(lead.estadoLeadUpdatedAt)}`;
}

/** "Orden de Ana Pérez · Confirmada · 89.900" (o el aviso de que no hay órdenes). */
export function describeUltimaOrden(orden: LeadOrdenResumen | null): string {
  if (!orden) return 'Todavía no tiene órdenes';

  return `Orden de ${orden.clienteNombre} · ${orderStatus[orden.estado].label} · ${formatNumber(
    orden.total,
  )}`;
}

/** Una fila del historial: "Frío → Cliente". */
export function describeHistorialEntry(entry: LeadHistorialEntry): string {
  const destino = leadStatus[entry.estadoNuevo].label;
  const origen = entry.estadoAnterior ? leadStatus[entry.estadoAnterior].label : null;

  return origen ? `${origen} → ${destino}` : `Nace en ${destino}`;
}

/** Quién hizo el cambio y con qué motivo (la segunda línea de la fila del historial). */
export function describeHistorialContext(entry: LeadHistorialEntry): string {
  const motivo = entry.motivo?.trim();
  const autor = entry.userId ? 'un usuario del equipo' : 'el clasificador automático';

  return `${originLabel[entry.origen]} (${autor})${motivo ? ` · ${motivo}` : ''}`;
}

/** Un mensaje del detalle: "Cliente: hola" (colapsado a una línea). */
export function describeMessage(mensaje: LeadMensajeReciente): string {
  return `${messageAuthorLabel[mensaje.direccion]}: ${collapseText(mensaje.contenido)}`;
}

/** "12 leads: 4 frío · 3 tibio · 2 caliente · 3 cliente" — el resumen del tablero. */
export function summarizeKanban(kanban: Kanban): string {
  const porEstado = kanban.columnas
    .map(
      (columna) =>
        `${formatNumber(columna.total)} ${leadStatus[columna.estadoLead].label.toLowerCase()}`,
    )
    .join(' · ');

  return `${countLabel(kanban.total, 'lead', 'leads')}: ${porEstado}`;
}

/** Resumen de la vista de tabla (con el total real, que puede ser mayor que la página). */
export function summarizeLeads(lista: ListaLeads): string {
  const porEstado = (Object.keys(lista.totalesPorEstado) as EstadoLead[])
    .map(
      (estado) =>
        `${formatNumber(lista.totalesPorEstado[estado])} ${leadStatus[estado].label.toLowerCase()}`,
    )
    .join(' · ');

  return `${countLabel(lista.total, 'lead', 'leads')}: ${porEstado}`;
}

/** La columna tiene más leads de los que entraron en el límite (hay que subirlo para verlos). */
export function columnHasMore(columna: KanbanColumna, limitePorColumna: number): boolean {
  return columna.total > columna.items.length && limitePorColumna < 200;
}

/** "Mostrando 26-50 de 120 leads" (vista de tabla). */
export function describeRange(offset: number, count: number, total: number): string {
  if (total === 0) return 'Sin leads';

  const desde = offset + 1;
  const hasta = offset + count;

  return `Mostrando ${formatNumber(desde)}-${formatNumber(hasta)} de ${countLabel(
    total,
    'lead',
    'leads',
  )}`;
}

/** Una fila del resultado del recálculo masivo: "Frío → Caliente · Tiene una orden creada". */
export function describeCambioLead(cambio: CambioLead): string {
  return `${leadStatus[cambio.estadoAnterior].label} → ${
    leadStatus[cambio.estadoNuevo].label
  } · ${LEAD_MOTIVO_LABEL[cambio.motivo]}`;
}

/** El titular del recálculo masivo: cuántas revisó, cuántas cambiaron y a quién respetó. */
export function summarizeRecalculo(result: RecalculoMasivoResultado): string {
  const cabecera = `${countLabel(
    result.revisadas,
    'conversación revisada',
    'conversaciones revisadas',
  )} · ${countLabel(result.actualizadas, 'lead actualizado', 'leads actualizados')}`;

  return result.incluirManuales
    ? `${cabecera} (incluidos los reclasificados a mano)`
    : `${cabecera} (los reclasificados a mano quedaron intactos)`;
}

/** Qué pasó al devolver UN lead a la clasificación automática. */
export function describeRecalculoLead(result: RecalculoLeadResultado): string {
  const estado = leadStatus[result.analisis.estadoLead].label;
  const motivo = LEAD_MOTIVO_LABEL[result.analisis.motivo];

  if (!result.actualizado) {
    return `El lead ya estaba en «${estado}» y clasificado automáticamente (${motivo}): no hubo nada que guardar.`;
  }

  return `El lead volvió a la clasificación automática: «${estado}» (${motivo}).`;
}

/**
 * Nombre de archivo de la descarga de clientes: `clientes-mi-negocio-2026-09-17.csv`.
 *
 * Espejo de `csvNombreArchivo` (`backend/src/lib/csv.ts`). El panel lo vuelve a armar porque el
 * nombre del negocio da contexto y el JS NO puede leer el `Content-Disposition` del backend cuando
 * está en otro origen (el navegador solo expone las cabeceras de la lista blanca de CORS). Solo
 * letras, números y guiones: un nombre con `/`, `\`, `"` o `;` rompería el guardado del navegador.
 */
export function nombreArchivoClientes(nombreEmpresa: string | null, fecha = new Date()): string {
  const base = nombreEmpresa ? `clientes-${nombreEmpresa}` : 'clientes';

  const limpio = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

  return `${limpio.length > 0 ? limpio : 'clientes'}-${fecha.toISOString().slice(0, 10)}.csv`;
}

/** Qué pasó con la descarga de la lista de clientes (y qué hacer si quedó truncada). */
export function describeDescargaClientes(resultado: {
  filas: number;
  truncado: boolean;
  maxFilas: number;
}): string {
  if (resultado.filas === 0) {
    return 'El archivo salió vacío: ningún lead cumple esos filtros.';
  }

  const cabecera = `${countLabel(resultado.filas, 'cliente descargado', 'clientes descargados')}.`;

  if (!resultado.truncado) return cabecera;

  const tope = resultado.maxFilas > 0 ? formatNumber(resultado.maxFilas) : 'el configurado';
  return `${cabecera} El negocio tiene más filas que el tope (${tope}): acota el rango de fechas y descarga el resto en otro archivo.`;
}

/* ---------------------------------------------------------------------------
 * Purga de conversaciones (borrado por lotes, solo ADMIN)
 * ------------------------------------------------------------------------- */

/**
 * Qué va a pasar (simulación) o qué pasó de verdad (corrida confirmada).
 *
 * El texto no usa la palabra «purgar» a secas: el usuario tiene que entender que se BORRAN
 * conversaciones y que las que tienen órdenes no se tocan aunque cumplan el filtro.
 */
export function describePurga(resultado: PurgaLeadsResultado): string {
  const ventana = `más de ${formatNumber(resultado.diasSinActividad)} días sin actividad`;
  const protegidas =
    resultado.protegidas > 0
      ? ` ${countLabel(resultado.protegidas, 'quedó protegida', 'quedaron protegidas')} por tener órdenes.`
      : '';

  if (!resultado.confirmado) {
    if (resultado.seleccionadas === 0) {
      return `No hay conversaciones para borrar con ${ventana}.${protegidas}`;
    }

    return `Se borrarían ${countLabel(
      resultado.seleccionadas,
      'conversación',
      'conversaciones',
    )} con ${ventana}, de ${countLabel(resultado.candidatas, 'candidata', 'candidatas')}.${protegidas}`;
  }

  const pendientes = resultado.candidatas - resultado.eliminadas;

  return `Se borraron ${countLabel(
    resultado.eliminadas,
    'conversación',
    'conversaciones',
  )} con ${ventana}.${
    pendientes > 0
      ? ` Quedan ${countLabel(
          pendientes,
          'candidata',
          'candidatas',
        )} para otra corrida (repite con un tope más alto si quieres borrarlas todas).`
      : ''
  }${protegidas}`;
}

/** Una línea de la lista de ejemplos: quién, en qué estado, cuántos mensajes y cuándo se movió. */
export function describePurgaEjemplo(ejemplo: PurgaEjemplo): string {
  return `${ejemplo.contacto} · ${leadStatus[ejemplo.estadoLead].label} · ${countLabel(
    ejemplo.mensajes,
    'mensaje',
    'mensajes',
  )} · ${formatRelativeTime(ejemplo.ultimoMovimiento)}`;
}

